import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { ConditionHistoryView } from "@/components/ConditionHistoryView";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Loader2, Lock, AlertTriangle, History } from "lucide-react";

export default function ConditionHistory() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { openUpgrade } = useTier();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error | locked | notfound

  useEffect(() => {
    let ok = true;
    api.get(`/properties/${propertyId}/condition-history`)
      .then((r) => ok && (setData(r.data), setState("ok")))
      .catch((e) => {
        if (!ok) return;
        const s = e?.response?.status;
        setState(s === 402 ? "locked" : s === 404 ? "notfound" : "error");
      });
    return () => { ok = false; };
  }, [propertyId]);

  // Resolve API records into view-ready moments (authed image URLs). Grouping stays server-side.
  const groups = (data?.groups || []).map((g) => ({
    ...g,
    moments: g.moments.map((m) => ({
      ...m,
      thumbnailUrl: fileUrl(m.storage_path),
      note: m.caption || null,
    })),
  }));

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24" data-testid="condition-history-page">
      <div className="max-w-3xl mx-auto px-4 pt-5">
        <button data-testid="ch-back-btn" onClick={() => navigate(`/property/${propertyId}`)}
          className="flex items-center gap-2 text-sm text-[#475569] hover:text-navy mb-4">
          <ChevronLeft size={18} /> Back to property
        </button>

        {state === "loading" && (
          <div className="grid place-items-center py-24" data-testid="ch-loading"><Loader2 className="animate-spin text-brand" size={26} /></div>
        )}

        {state === "locked" && (
          <div className="rounded-2xl border border-brand/20 bg-brand/[0.04] p-6 text-center" data-testid="ch-locked">
            <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand/10 text-brand mx-auto"><History size={24} /></span>
            <h1 className="mt-3 font-heading text-xl font-bold text-navy flex items-center justify-center gap-2">Condition History <Lock size={16} className="text-[#94A3B8]" /></h1>
            <p className="mt-1 text-sm text-[#64748B]">Condition History is a TenantSeal Plus feature. Your underlying evidence stays free to view any time.</p>
            <Button data-testid="ch-upgrade" onClick={() => openUpgrade("condition_history")} className="mt-4 h-10 rounded-xl bg-navy text-white">Upgrade to Plus</Button>
          </div>
        )}

        {state === "notfound" && (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 text-center text-sm text-[#64748B]" data-testid="ch-notfound">
            We couldn't find this property.
          </div>
        )}

        {state === "error" && (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 text-center" data-testid="ch-error">
            <AlertTriangle size={22} className="text-amber-500 mx-auto" />
            <p className="mt-2 text-sm text-[#64748B]">We couldn't load your Condition History. Please try again.</p>
            <Button variant="outline" data-testid="ch-error-retry" onClick={() => { setState("loading"); api.get(`/properties/${propertyId}/condition-history`).then((r) => (setData(r.data), setState("ok"))).catch(() => setState("error")); }} className="mt-3 h-10 rounded-xl">Retry</Button>
          </div>
        )}

        {state === "ok" && groups.length === 0 && (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-8 text-center" data-testid="ch-empty">
            <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand/10 text-brand mx-auto"><History size={24} /></span>
            <p className="mt-3 text-sm font-semibold text-navy">Not enough history yet</p>
            <p className="mt-1 text-xs text-[#64748B]">
              {data?.excluded_unconfirmed_count > 0
                ? `${data.excluded_unconfirmed_count} during-tenancy record(s) need a confirmed room or location to appear here.`
                : "Add another documented checkup for the same room or location to build its history."}
            </p>
          </div>
        )}

        {state === "ok" && groups.length > 0 && (
          <ConditionHistoryView
            address={null}
            groups={groups}
            excludedCount={data?.excluded_unconfirmed_count || 0}
            linkedCount={(data?.linked_property_ids || []).length}
            onOpenEvidence={(m) => navigate(m.existing_route || `/property/${propertyId}`)}
            onOpenProofReplay={(g) => g.proof_replay_route && navigate(g.proof_replay_route)}
          />
        )}
      </div>
    </div>
  );
}
